import React from "react";

import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Divider, Grid } from "@material-ui/core";
import { FieldArray, useField } from "formik";
import { isObject, uniq } from "lodash";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { SlimButton } from "components/Button";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { DeploymentSelectField } from "components/Form/Fields/DeploymentField";
import { PropertySelectField } from "components/Form/Fields/PropertyField";
import {
  SaleSubTypeField,
  SaleTypeField,
  SpeciesField,
} from "components/Form/Fields/SaleFilter";
import { Column } from "components/Layout";

import {
  getActiveLivestockAgentDeployment,
  getDefaultBillingSettings,
} from "selectors";

import { validatePercentage } from "lib/validation";

import { Error } from "./Error";
import { PercentageInput } from "./Input";
import { Label } from "./Label";
import { Required } from "./layout";
import { withNamespace } from "./lib";

export enum ValidationMessage {
  REQUIRED = "Required",
  REQUIRES_SALE_TYPE = "Requires Sale Type to be selected.",
  REQUIRES_SALE_TYPE_SPECIES = "Requires Sale Type and Species to both be selected.",
  NON_UNIQUE = "This combination of Deployment, Property, Sale Type, Species and Sale Sub Type is not unique.",
}

export const sellViaExAgentRebatesValidation = Yup.array()
  .nullable()
  .of(
    Yup.object().shape({
      deploymentId: Yup.number().required(ValidationMessage.REQUIRED),
      propertyId: Yup.string().nullable(true),
      saleType: Yup.string().nullable(true),
      speciesId: Yup.number()
        .nullable(true)
        .test(
          "saleType-required",
          ValidationMessage.REQUIRES_SALE_TYPE,
          function (value: number | null) {
            if (!value) {
              return true;
            }
            const { saleType } = this.parent;
            return !!saleType;
          },
        ),
      saleSubTypeId: Yup.number()
        .nullable(true)
        .test(
          "saleType-species-required",
          ValidationMessage.REQUIRES_SALE_TYPE_SPECIES,
          function (value: number | null) {
            if (!value) {
              return true;
            }
            const { saleType, speciesId } = this.parent;
            return !!saleType && !!speciesId;
          },
        ),
      sellViaExAgentRebateRate: validatePercentage(9, 6).required(
        ValidationMessage.REQUIRED,
      ),
    }),
  )
  .test(
    "non-dupe",
    ValidationMessage.NON_UNIQUE,
    value =>
      uniq(
        value?.map(
          rebate =>
            `${rebate.deploymentId ?? ""}|${rebate.propertyId ?? ""}|${
              rebate.saleType ?? ""
            }|${rebate.speciesId ?? ""}|${rebate.saleSubTypeId ?? ""}`,
        ),
      ).length === (value?.length || 0),
  );

interface SellViaExAgentRebateProps {
  index: number;
  name: string;
  namespace?: string;
  removeRow: (index: number) => void;
}

function SellViaExAgentRebate(props: SellViaExAgentRebateProps) {
  const { index, name, namespace, removeRow } = props;
  const ns = `${withNamespace(namespace, name)}.${index}`;
  const ownDeploymentId = useSelector(getActiveLivestockAgentDeployment)?.id;

  return (
    <>
      <Grid item xs={6} md={2}>
        <DeploymentSelectField
          name={`${ns}.deploymentId`}
          label=""
          required
          restrictToOwnDeployments={false}
          retrictToSaleDeployments={false}
          excludeDeploymentIds={ownDeploymentId ? [ownDeploymentId] : undefined}
        />
      </Grid>
      <Grid item xs={6} md={2}>
        <PropertySelectField name={`${ns}.propertyId`} label="" />
      </Grid>
      <Grid item xs={6} md={2}>
        <SaleTypeField namespace={ns} />
      </Grid>
      <Grid item xs={6} md={2}>
        <SpeciesField namespace={ns} />
      </Grid>
      <Grid item xs={12} md={2}>
        <SaleSubTypeField namespace={ns} />
      </Grid>
      <Grid item xs={6} md={1}>
        <PercentageInput name={`${ns}.sellViaExAgentRebateRate`} />
      </Grid>
      <Grid item xs={6} md={1} container justifyContent="flex-end">
        <SlimButton
          onClick={() => removeRow(index)}
          backgroundColor="deleteRed"
          type="button"
        >
          <ButtonIcon icon={faTrash} />
          &nbsp;Remove
        </SlimButton>
      </Grid>
      <Grid xs={12} item>
        <Divider />
      </Grid>
    </>
  );
}

const generateDefaultRebate = (
  billingSettingsId: string,
): Partial<SellViaExAgentRebate> => ({
  id: uuidv4(),
  billingSettingsId,
  deploymentId: null,
  propertyId: null,
  saleSubTypeId: null,
  saleType: null,
  speciesId: null,
  sellViaExAgentRebateRate: 0,
});

interface SellViaExAgentRebatesProps {
  namespace?: string;
  name?: string;
  label?: string;
  tooltip?: string;
}

export function SellViaExAgentRebates(props: SellViaExAgentRebatesProps) {
  const {
    namespace = "",
    name = "sellViaExAgentRebates",
    label = "Sell via External Agent Rebates",
    tooltip,
  } = props;
  const [field, meta, helpers] = useField(withNamespace(namespace, name));

  const value = field.value ?? [];
  const { error } = meta;

  const billingSettingsId = useSelector(getDefaultBillingSettings)?.id;

  if (!billingSettingsId) {
    return null;
  }

  return (
    <Column fullWidth fullHeight>
      {label ? <Label tooltip={tooltip}>{label}</Label> : null}
      <Column fullWidth fullHeight paddingHorizontal={3}>
        <FieldArray name={withNamespace(namespace, name)}>
          {arrayHelpers => {
            const addNew = () => {
              arrayHelpers.push(generateDefaultRebate(billingSettingsId));
            };
            const removeRow = (index: number) => {
              arrayHelpers.remove(index);
              helpers.setTouched(true);
            };
            return (
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <SlimButton onClick={addNew} type="button">
                    <ButtonIcon icon={faPlus} />
                    &nbsp;New Rebate
                  </SlimButton>
                </Grid>
                {!isObject(error) && error ? (
                  <Grid item xs={12}>
                    <Error>{error}</Error>
                  </Grid>
                ) : null}
                {value.length > 0 ? (
                  <>
                    <Grid item xs={6} md={2}>
                      <Label>
                        Deployment
                        <Required />
                      </Label>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Label>Property</Label>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Label>Sale Type</Label>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Label>Species</Label>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Label>Sale Sub Type</Label>
                    </Grid>
                    <Grid item xs={6} md={1}>
                      <Label>
                        Rate
                        <Required />
                      </Label>
                    </Grid>
                    <Grid item xs={6} md={1}>
                      &nbsp;
                    </Grid>
                    <Grid xs={12} item>
                      <Divider />
                    </Grid>
                    {value.map((rebate: SellViaExAgentRebate, idx: number) => (
                      <SellViaExAgentRebate
                        index={idx}
                        removeRow={removeRow}
                        name={name}
                        namespace={namespace}
                        key={rebate.id}
                      />
                    ))}
                  </>
                ) : null}
              </Grid>
            );
          }}
        </FieldArray>
      </Column>
    </Column>
  );
}
